/**
 * Ce fichier contient des utils pour la map
 *
 * © Moustapha HENNAWI - Click and Take off - 2021
 */

import mapboxgl from 'mapbox-gl';
import { parse, stringify } from 'wellknown';
import circle from '@turf/circle';

import { map } from './Map';
/*-------------------------------------------------------------------------------------------*/

export const loadImage = (url) =>
  new Promise((imageLoaded) => {
    const image = new Image();
    image.onload = () => imageLoaded(image);
    image.src = url;
  });

/*-------------------------------------------------------------------------------------------*/

const canvasTintImage = (image, color) => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width * devicePixelRatio;
  canvas.height = image.height * devicePixelRatio;
  canvas.style.width = `${image.width}px`;
  canvas.style.height = `${image.height}px`;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  context.save();
  context.fillStyle = color;
  context.globalAlpha = 1;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = 'destination-atop';
  context.globalAlpha = 1;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  context.restore();

  return canvas;
};

export const prepareIcon = (background, icon, color) => {
  const canvas = document.createElement('canvas');
  canvas.width = background.width * devicePixelRatio;
  canvas.height = background.height * devicePixelRatio;
  canvas.style.width = `${background.width}px`;
  canvas.style.height = `${background.height}px`;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(background, 0, 0, canvas.width, canvas.height);

  if (icon) {
    const iconRatio = 0.5;
    const imageWidth = canvas.width * iconRatio;
    const imageHeight = canvas.height * iconRatio;
    if (navigator.userAgent.indexOf('Firefox') > 0) {
      context.drawImage(
        icon,
        (canvas.width - imageWidth) / 2,
        (canvas.height - imageHeight) / 2,
        imageWidth,
        imageHeight
      );
    } else {
      context.drawImage(
        canvasTintImage(icon, color),
        (canvas.width - imageWidth) / 2,
        (canvas.height - imageHeight) / 2,
        imageWidth,
        imageHeight
      );
    }
  }

  return context.getImageData(0, 0, canvas.width, canvas.height);
};

/*-------------------------------------------------------------------------------------------*/

export const reverseCoordinates = (it) => {
  if (!it) {
    return it;
  }
  if (Array.isArray(it)) {
    if (it.length === 2 && !Number.isNaN(it[0]) && !Number.isNaN(it[1])) {
      return [it[1], it[0]];
    }
    return it.map((it) => reverseCoordinates(it));
  }
  return {
    ...it,
    coordinates: reverseCoordinates(it.coordinates)
  };
};

export const geofenceToFeature = (item) => {
  let geometry;
  if (item.area.indexOf('CIRCLE') > -1) {
    const coordinates = item.area
      .replace(/CIRCLE|\(|\)|,/g, ' ')
      .trim()
      .split(/ +/);
    const options = { steps: 32, units: 'meters' };
    const polygon = circle(
      [Number(coordinates[1]), Number(coordinates[0])],
      Number(coordinates[2]),
      options
    );
    geometry = polygon.geometry;
  } else {
    geometry = reverseCoordinates(parse(item.area));
  }
  return {
    id: item.id,
    type: 'Feature',
    geometry,
    properties: {
      name: item.name,
      color: item.attributes.color || '#3366FF',
      height: item.altitudeMax,
      base: item.altitudeMin
    }
  };
};

export const geometryToArea = (geometry) => stringify(reverseCoordinates(geometry));
/*-------------------------------------------------------------------------------------------*/
// A collection of handler names
const mapInteractionHandlers = [
  'scrollZoom',
  'dragPan',
  'touchZoomRotate',
  'dragRotate',
  'keyboard',
  'boxZoom',
  'doubleClickZoom'
];

// Returns true if at least one of the above handlers is enabled
export const isInteractionEnabled = () =>
  mapInteractionHandlers.reduce(
    (isEnabled, nextHandler) => map[nextHandler].isEnabled() && isEnabled,
    true
  );

// Enable map interaction (all handlers)
export const enableInteraction = () => {
  // eslint-disable-next-line no-restricted-syntax
  for (const handler of mapInteractionHandlers) {
    map[handler].enable();
  }
};

// Disable map interaction (all handlers)
export const disableInteraction = () => {
  // eslint-disable-next-line no-restricted-syntax
  for (const handler of mapInteractionHandlers) {
    map[handler].disable();
  }
};
/*-------------------------------------------------------------------------------------------*/

export function mapFitBound(coord) {
  if (coord.length === 0) return;
  const bounds = new mapboxgl.LngLatBounds(coord[0], coord[0]);
  for (let i = 0; i < coord.length; i += 1) {
    bounds.extend(coord[i]);
  }
  map.fitBounds(bounds, {
    padding: 70
  });
}
